import {
  Select as MuiSelect,
  MenuItem,
  OutlinedInput,
  Box,
  Chip,
  Theme,
  Stack,
  Checkbox,
} from '@mui/material';
import { ChangeEvent, FC } from 'react';
import FormControl from '../form-control';
import { FormProps } from '..';
import { createUseStyles } from 'react-jss';
import { UiOption } from '@/lib/helpers';
import Text from '@/components/text';
import classNames from 'classnames';
import MoreCountList from '@/components/more-count-list';

interface Props extends FormProps {
  value: string[];
  options: UiOption[];
  onChange: (options: UiOption[], change: { value: string; enabled: boolean }) => void;
  placeholder?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  select: {
    '&.x-small': {
      '& .MuiSelect-select': {
        padding: '6px 32px 6px 14px',
        minHeight: 20,
      },
      '& #select-multiple-chip .MuiSelect-select': {
        paddingTop: 4,
        paddingBottom: 4,
        fontSize: 12,
      },
      '& $selectedChips': {
        height: 21,
        '& .MuiChip-root': {
          height: 21,
          lineHeight: 1,
        },
        '& .MuiChip-label': {
          fontSize: 11,
          paddingLeft: 6,
          paddingRight: 6,
        },
      },
    },
  },
  input: {
    '& fieldset': {
      borderWidth: '1px !important',
    },
    '&.Mui-disabled fieldset': {
      borderColor: `${theme.palette.grey[600]} !important`,
    },
    '& .MuiFormLabel-filled .MuiSelect-select': {
      paddingBottom: '15.5px',
    },
  },
  menuList: {
    '&.x-small $option .MuiCheckbox-root': {
      paddingTop: 2,
      paddingBottom: 2,
    },
    '&.x-small $option .MuiTypography-root': {
      fontSize: 12,
    },
  },
  option: {
    '& span.MuiCheckbox-root': {
      padding: '4px 4px 4px 0px',
    },
  },
  selectedChips: {
    height: 23,
    overflow: 'hidden',
    position: 'relative',
  },
}));

const SelectMulti: FC<Props> = ({
  name,
  value = [''],
  options,
  label = '',
  disabled = false,
  autoFocus = false,
  onChange,
  fullWidth,
  error = '',
  size = 'medium',
  readonly = false,
  placeholder = '',
}) => {
  const styles = useStyles();
  const isXSmall = size === 'x-small';
  const isSmall = ['small', 'tiny', 'x-small'].includes(size);
  const muiSize = isSmall ? 'small' : 'medium';

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // convert the heinous return value of the MUI Select ([any,any,any..., string]) into UiOption[]
    const values = (event.target.value || []) as (UiOption | string)[];
    const selected: UiOption[] = [];
    let newValue = '';

    values.forEach((opt) => {
      if (typeof opt === 'string') {
        newValue = opt;
        return;
      }
      selected.push(opt);
    });

    const existIndex = selected.findIndex(({ value }) => value === newValue);
    const enabled = existIndex === -1;
    if (existIndex === -1) {
      const newOption = options.find(({ value }) => value === newValue);
      if (newOption) {
        selected.push(newOption);
      }
    } else {
      selected.splice(existIndex, 1);
    }
    onChange(selected, { value: newValue, enabled });
  };

  const labelId = `${name}-label`;
  const valueOpts = options.filter((opt) => value.includes(opt.value));
  const compClass = classNames(styles.select, size);

  return (
    <FormControl label={label} error={error} name={name}>
      <MuiSelect
        className={compClass}
        labelId={labelId}
        id={name}
        multiple
        disabled={disabled || readonly}
        autoFocus={autoFocus}
        value={valueOpts}
        onChange={handleChange}
        displayEmpty={!!placeholder}
        input={<OutlinedInput id="select-multiple-chip" label={label} className={styles.input} />}
        renderValue={(selected: UiOption[]) => {
          if (!selected.length && placeholder) {
            return (
              <Text size={isXSmall ? 'small' : 'medium'} dotdot>
                {placeholder}
              </Text>
            );
          }
          return (
            <Box display="flex" gap={0.5} className={styles.selectedChips}>
              <MoreCountList>
                {selected.map(({ label, value }) => (
                  <Chip key={value} label={label} size="small" />
                ))}
              </MoreCountList>
            </Box>
          );
        }}
        fullWidth={fullWidth}
        error={!!error}
        size={muiSize}
        MenuProps={{
          MenuListProps: {
            className: `${styles.menuList} ${size}`,
          },
        }}
      >
        {options.map(({ label, value: optValue, disabled = false }) => {
          return (
            <MenuItem key={optValue} value={optValue} disabled={disabled} className={styles.option}>
              <Stack direction="row" gap={1} alignItems="center">
                <Checkbox checked={value.includes(optValue)} disableRipple />
                <Text size={isXSmall ? 'small' : 'medium'}>{label}</Text>
              </Stack>
            </MenuItem>
          );
        })}
      </MuiSelect>
    </FormControl>
  );
};

export default SelectMulti;
